/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * Organization Theme Application Utility
 *
 * Applies a complete organization theme from the theme configuration.
 * This is used when an organization has selected a predefined theme
 * (as opposed to custom primary/secondary colors).
 */

import type { OrganizationTheme } from "@/lib/themes";
import { generateComplementaryColor } from "@/lib/utils/colors/color-harmony";
import {
  generateColorScaleFromHsl,
  getContrastingTextColor,
  parseHsl,
} from "@/lib/utils/colors/color-utils";
import { logger } from "@/lib/utils/logger";
import { refreshDesignTokenCache } from "@/components/ui/foundation/design-tokens";
import { applyThemeVars, hasThemeStyleTag } from "./theme-style-tag";
import { applyOrganizationColors } from "./apply-theme-colors";

/**
 * Apply a complete organization theme to CSS variables
 *
 * @param theme - The organization theme to apply
 * @param isDark - Whether dark mode is active
 */
export function applyOrganizationTheme(
  theme: OrganizationTheme,
  isDark: boolean = false,
): void {
  // Dark mode: delegate to applyOrganizationColors which has full dark mode
  // color generation (background, foreground, card, muted, surfaces, semantics).
  // Theme cssVariables only contain light-mode values, so applying them in dark
  // mode would override the .dark CSS from globals.css with white backgrounds.
  if (isDark) {
    applyOrganizationColors(
      theme.colors.primary.hex,
      theme.colors.secondary.hex,
      true,
    );
    return;
  }

  try {
    const vars: Record<string, string> = {};

    // Apply all CSS variables from the theme configuration (light mode only)
    Object.entries(theme.cssVariables).forEach(([property, value]) => {
      vars[property] = value;
    });

    // Generate and apply color scales for primary
    const primaryHsl = theme.cssVariables["--primary"];
    if (primaryHsl) {
      const primaryScale = generateColorScaleFromHsl(primaryHsl);
      Object.entries(primaryScale).forEach(([shade, hsl]) => {
        vars[`--primary-${shade}`] = hsl;
      });

      // Apply state colors for primary
      const primaryParsed = parseHsl(primaryHsl);
      const primaryHover = `${primaryParsed.h} ${primaryParsed.s}% ${Math.min(primaryParsed.l + 5, 95)}%`;
      const primaryActive = `${primaryParsed.h} ${primaryParsed.s}% ${Math.max(primaryParsed.l - 5, 5)}%`;
      const primaryDisabled = `${primaryParsed.h} ${Math.max(primaryParsed.s - 30, 0)}% ${primaryParsed.l}%`;

      vars["--primary-hover"] = primaryHover;
      vars["--primary-active"] = primaryActive;
      vars["--primary-disabled"] = primaryDisabled;
    }

    // Generate and apply color scales for secondary
    const secondaryHsl = theme.cssVariables["--secondary"];
    if (secondaryHsl) {
      const secondaryScale = generateColorScaleFromHsl(secondaryHsl);
      Object.entries(secondaryScale).forEach(([shade, hsl]) => {
        vars[`--secondary-${shade}`] = hsl;
      });

      // Apply state colors for secondary
      const secondaryParsed = parseHsl(secondaryHsl);
      const secondaryHover = `${secondaryParsed.h} ${secondaryParsed.s}% ${Math.min(secondaryParsed.l + 5, 95)}%`;
      const secondaryActive = `${secondaryParsed.h} ${secondaryParsed.s}% ${Math.max(secondaryParsed.l - 5, 5)}%`;
      const secondaryDisabled = `${secondaryParsed.h} ${Math.max(secondaryParsed.s - 30, 0)}% ${secondaryParsed.l}%`;

      vars["--secondary-hover"] = secondaryHover;
      vars["--secondary-active"] = secondaryActive;
      vars["--secondary-disabled"] = secondaryDisabled;
    }

    // Accent and ring — derived from primary (matching legacy path)
    if (primaryHsl) {
      const pp = parseHsl(primaryHsl);
      vars["--accent"] = primaryHsl;
      vars["--accent-foreground"] = getContrastingTextColor(pp);
      vars["--ring"] = primaryHsl;
    }

    // Tertiary color + scale + state colors
    {
      let tertiaryHsl = theme.cssVariables["--tertiary"];
      if (!tertiaryHsl && primaryHsl) {
        tertiaryHsl = generateComplementaryColor(parseHsl(primaryHsl), {
          saturationAdjust: -10,
          maintainLightness: true,
        });
        vars["--tertiary"] = tertiaryHsl;
        vars["--tertiary-foreground"] = getContrastingTextColor(
          parseHsl(tertiaryHsl),
        );
      }
      if (tertiaryHsl) {
        const tertiaryScale = generateColorScaleFromHsl(tertiaryHsl);
        Object.entries(tertiaryScale).forEach(([shade, hsl]) => {
          vars[`--tertiary-${shade}`] = hsl;
        });
        const tp = parseHsl(tertiaryHsl);
        vars["--tertiary-hover"] =
          `${tp.h} ${tp.s}% ${Math.min(tp.l + 5, 95)}%`;
        vars["--tertiary-active"] =
          `${tp.h} ${tp.s}% ${Math.max(tp.l - 5, 5)}%`;
        vars["--tertiary-disabled"] =
          `${tp.h} ${Math.max(tp.s - 30, 0)}% ${tp.l}%`;
      }
    }

    // Generate and apply color scales for semantic colors
    const semanticKeys = ["success", "warning", "error", "info"] as const;
    for (const key of semanticKeys) {
      const hsl = theme.cssVariables[`--${key}`];
      if (hsl) {
        const scale = generateColorScaleFromHsl(hsl);
        Object.entries(scale).forEach(([shade, shadeHsl]) => {
          vars[`--${key}-${shade}`] = shadeHsl;
        });
        const parsed = parseHsl(hsl);
        vars[`--${key}-hover`] =
          `${parsed.h} ${parsed.s}% ${Math.min(parsed.l + 5, 95)}%`;
        vars[`--${key}-active`] =
          `${parsed.h} ${parsed.s}% ${Math.max(parsed.l - 5, 5)}%`;
        vars[`--${key}-disabled`] =
          `${parsed.h} ${Math.max(parsed.s - 30, 0)}% ${parsed.l}%`;
      }
    }

    // Apply surface colors (Material Design 3 hierarchy — all 7 vars)
    const bgHsl = theme.cssVariables["--background"] ?? "0 0% 100%";
    const bgParsed = parseHsl(bgHsl);
    vars["--surface"] = `${bgParsed.h} ${bgParsed.s}% ${bgParsed.l}%`;
    vars["--surface-variant"] =
      `${bgParsed.h} ${bgParsed.s}% ${Math.max(0, Math.min(100, bgParsed.l - 2))}%`;
    vars["--surface-container-lowest"] =
      `${bgParsed.h} ${bgParsed.s}% ${Math.max(0, Math.min(100, bgParsed.l - 1))}%`;

    const cardHsl = theme.cssVariables["--card"];
    if (cardHsl) {
      vars["--surface-container"] = cardHsl;
      const cardParsed = parseHsl(cardHsl);
      vars["--surface-container-low"] =
        `${cardParsed.h} ${cardParsed.s}% ${Math.min(cardParsed.l + 6, 95)}%`;
      vars["--surface-container-high"] =
        `${cardParsed.h} ${cardParsed.s}% ${Math.max(cardParsed.l - 4, 5)}%`;
      vars["--surface-container-highest"] =
        `${cardParsed.h} ${cardParsed.s}% ${Math.max(cardParsed.l - 8, 5)}%`;
    }

    applyThemeVars(vars);
    refreshDesignTokenCache();

    logger.debug("Applied organization theme", {
      themeId: theme.id,
      themeName: theme.name,
      isDark,
    });
  } catch (error) {
    logger.error("Failed to apply organization theme", { error, theme });
    throw error;
  }
}

/**
 * Check if the current document has a theme applied
 */
export function hasThemeApplied(): boolean {
  return hasThemeStyleTag();
}
